/**
 * App.jsx — Root application component
 *
 * Phase 2: Integrates MSAL authentication for dual M365 accounts.
 * Shows the auth panel until both accounts are connected,
 * then transitions to the calendar view (Phase 4).
 *
 * App state flow:
 *   1. MSAL initializing → loading screen
 *   2. Accounts not connected → AuthPanel (login buttons)
 *   3. Both connected → Calendar view (placeholder for now)
 */

import React from 'react';
import { useAuth } from './hooks/useAuth';
import AuthPanel from './components/AuthPanel';
import './index.css';

function App() {
  const {
    cal1,
    cal2,
    loginCal1,
    loginCal2,
    logoutCal1,
    logoutCal2,
    isReady,
    initError,
    bothConnected,
  } = useAuth();

  // MSAL still initializing
  if (!isReady) {
    return (
      <div className="app">
        <div className="loading-screen">
          {initError ? (
            <div className="init-error">
              <h2>Initialization Error</h2>
              <p>{initError}</p>
              <p>Try refreshing the page.</p>
            </div>
          ) : (
            <>
              <div className="spinner large" />
              <p>Initializing CalBridge...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <h1>CalBridge</h1>
          <span className="version">v0.2.0</span>
        </div>
        {bothConnected && (
          <div className="header-accounts">
            <span className="header-account" style={{ '--dot-color': '#4A90D9' }}>
              {cal1.profile?.displayName}
            </span>
            <span className="header-separator">|</span>
            <span className="header-account" style={{ '--dot-color': '#D94A4A' }}>
              {cal2.profile?.displayName}
            </span>
          </div>
        )}
      </header>

      <main className="app-main">
        {!bothConnected ? (
          <AuthPanel
            cal1={cal1}
            cal2={cal2}
            loginCal1={loginCal1}
            loginCal2={loginCal2}
            logoutCal1={logoutCal1}
            logoutCal2={logoutCal2}
          />
        ) : (
          <div className="calendar-placeholder">
            <h2>Calendars Connected</h2>
            <p>
              <strong>{cal1.profile?.displayName}</strong> ({cal1.profile?.mail})
              and <strong>{cal2.profile?.displayName}</strong> ({cal2.profile?.mail})
              are ready.
            </p>
            <p className="next-phase">Calendar view coming in Phase 3.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
